import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { CheckCircle2, AlertCircle, ChevronDown, X } from "lucide-react";
import storage from "utils/Storage";
import axios from "axios";
import { toast } from "react-toastify";
import { API as API_URL } from 'utils/Helper';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background-color: #292d2e;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #3a4142;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  margin: 0;
`;

const StatusBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  background-color: ${props => props.verified ? "rgba(31, 193, 107, 0.1)" : "rgba(239, 68, 68, 0.1)"};
  color: ${props => props.verified ? "#1fc16b" : "#ef4444"};
  border: 1px solid ${props => props.verified ? "rgba(31, 193, 107, 0.2)" : "rgba(239, 68, 68, 0.2)"};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #3a4142;
  margin: 20px 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: bold;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  background-color: #1e2024;
  border: 1px solid #3a4142;
  border-radius: 6px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  
  &:focus {
    border-color: #1fc16b;
  }

  &:read-only {
    opacity: 0.6;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Select = styled.select`
  width: 100%;
  appearance: none;
  background-color: #1e2024;
  border: 1px solid #3a4142;
  border-radius: 6px;
  padding: 12px 16px;
  padding-right: 40px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  outline: none;

  &:focus {
    border-color: #1fc16b;
  }
`;

const SelectIcon = styled(ChevronDown)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #9ca3af;
  pointer-events: none;
`;

const ActionButton = styled.button`
  height: 48px;
  padding: 0 24px;
  border-radius: 6px;
  background-color: #1fc16b;
  color: #000;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 10px;
  border: none;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #1ab060;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Description = styled.p`
  color: #9ca3af;
  font-size: 14px;
  margin-top: 8px;
  line-height: 1.6;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1e2024;
  border-radius: 8px;
  padding: 30px;
  width: 400px;
  max-width: 90%;
  position: relative;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  border: 1px solid #3a4142;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  &:hover { color: #fff; }
`;

const Account = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [email, setEmail] = useState(storage.getKey('email') || "user@example.com");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  
  const [socialProvider, setSocialProvider] = useState(null);
  const [newSocialPassword, setNewSocialPassword] = useState("");
  
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpType, setOtpType] = useState(""); // 'email' or 'phone'
  const [otpValue, setOtpValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState(null);

  // Decode JWT to get UID safely
  let uid = null;
  try {
    const token = storage.getKey('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      uid = payload.id || payload.uid;
    }
  } catch (e) {
    console.error("Could not parse token");
  }
  // Fallback if somehow uid was stored directly
  if (!uid) {
    uid = storage.getKey('id');
  }

  useEffect(() => {
    fetchProfile();
    fetchAuthConfig();
  }, []);

  const fetchAuthConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/requirements?type=register`);
      if (res.data.status && res.data.data) {
        setAuthConfig(res.data.data.fields_config || res.data.data);
      }
    } catch (err) {
      console.error("Error fetching auth config", err);
    }
  };

  const fetchProfile = async () => {
    if (!uid) return;
    try {
      const res = await axios.get(`${API_URL}/api/myprofile/${uid}`);
      if (res.data.status && res.data.data.length > 0) {
        const user = res.data.data[0];
        setEmail(user.email || "");
        if (user.phone) {
          // Assume phone might include country code or handle parsing if needed
          setPhoneNumber(user.phone);
        }
        setIsEmailVerified(!!user.is_email_verified);
        setIsPhoneVerified(!!user.is_phone_verified);
        if (user.profile && typeof user.profile === 'object' && user.profile.provider) {
          setSocialProvider(user.profile.provider);
        }
      }
    } catch (err) {
      console.error("Error fetching profile", err);
    }
  };

  const handleSendOtp = async (type) => {
    setIsLoading(true);
    try {
      let endpoint = type === 'email' ? '/send-otp' : '/send-phone-otp';
      let payload = type === 'email' ? { email } : { phone: countryCode + phoneNumber };
      
      const res = await axios.post(`${API_URL}${endpoint}`, payload);
      if (res.data.status) {
        toast.success(res.data.message || `OTP sent to your ${type}`);
        setOtpType(type);
        setOtpValue("");
        setShowOtpModal(true);
      } else {
        toast.error(res.data.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred");
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpValue) return toast.error("Please enter OTP");
    setIsLoading(true);
    try {
      let endpoint = otpType === 'email' ? '/verify-email-otp' : '/verify-phone-otp';
      let payload = otpType === 'email' 
        ? { email, otp: otpValue, uid } 
        : { phone: countryCode + phoneNumber, otp: otpValue, uid };

      const res = await axios.post(`${API_URL}${endpoint}`, payload);
      if (res.data.status) {
        toast.success(res.data.message || "Verified successfully!");
        setShowOtpModal(false);
        if (otpType === 'email') setIsEmailVerified(true);
        if (otpType === 'phone') setIsPhoneVerified(true);
      } else {
        toast.error(res.data.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid OTP");
    }
    setIsLoading(false);
  };

  const handleSetSocialPassword = async () => {
    if (!newSocialPassword || newSocialPassword.length < 6) {
      return toast.error("Password must be at least 6 characters long");
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/set-social-password`, {
        uid,
        newPassword: newSocialPassword
      });
      if (res.data.message) {
        toast.success(res.data.message);
        setSocialProvider(null);
        setNewSocialPassword("");
      } else {
        toast.error(res.data.error || "Failed to set password");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to set password");
    }
    setIsLoading(false);
  };

  return (
    <PageContainer>
      {/* Email Verification */}
      <Card>
        <CardHeader>
          <TitleGroup>
            <Title>Email</Title>
            {isEmailVerified ? (
              <StatusBadge verified>
                <CheckCircle2 /> Verified
              </StatusBadge>
            ) : (
              <StatusBadge>
                <AlertCircle /> Not Verified
              </StatusBadge>
            )}
          </TitleGroup>
        </CardHeader>
        <Divider />
        <FormGroup>
          <Label>Email Address</Label>
          <InputRow>
            <Input readOnly value={email} />
            {!isEmailVerified && (
              <ActionButton 
                onClick={() => handleSendOtp('email')}
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Verify Now"}
              </ActionButton>
            )}
          </InputRow>
        </FormGroup>
      </Card>

      {/* Phone Verification */}
      <Card>
        <CardHeader>
          <TitleGroup>
            <Title>Phone Number</Title>
            {isPhoneVerified ? (
              <StatusBadge verified>
                <CheckCircle2 /> Verified
              </StatusBadge>
            ) : (
              <StatusBadge>
                <AlertCircle /> Unlinked
              </StatusBadge>
            )}
          </TitleGroup>
        </CardHeader>
        <Description>
          Please link your phone number to enhance your account security and enable faster withdrawals.
        </Description>
        <Divider />
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <FormGroup>
            <Label>Country Code</Label>
            <SelectContainer>
              <Select 
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                <option value="+91">+91 India</option>
                <option value="+1">+1 USA / Canada</option>
                <option value="+44">+44 UK</option>
                <option value="+31">+31 Netherlands</option>
                <option value="+33">+33 France</option>
              </Select>
              <SelectIcon />
            </SelectContainer>
          </FormGroup>
          
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Phone Number</Label>
            <InputRow>
              <Input 
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your mobile number"
              />
              <ActionButton 
                disabled={!phoneNumber || isLoading}
                onClick={() => handleSendOtp('phone')}
              >
                {isPhoneVerified ? "Update" : (isLoading ? "Wait..." : "Verify & Link")}
              </ActionButton>
            </InputRow>
          </FormGroup>
        </div>
      </Card>

      {/* Social Login Conversion */}
      {socialProvider && authConfig?.[socialProvider]?.allowConversionToNormal && (
        <Card>
          <CardHeader>
            <TitleGroup>
              <Title>Convert to Normal Login</Title>
              <StatusBadge>
                <AlertCircle /> Social Login ({socialProvider})
              </StatusBadge>
            </TitleGroup>
          </CardHeader>
          <Description>
            You are currently logged in via a social account ({socialProvider}). Set a password below to convert your account into a standard email/password login.
          </Description>
          <Divider />
          <FormGroup>
            <Label>New Password</Label>
            <InputRow>
              <Input 
                type="password"
                value={newSocialPassword}
                onChange={(e) => setNewSocialPassword(e.target.value)}
                placeholder="Enter a new secure password"
              />
              <ActionButton 
                disabled={!newSocialPassword || isLoading}
                onClick={handleSetSocialPassword}
              >
                {isLoading ? "Setting..." : "Set Password"}
              </ActionButton>
            </InputRow>
          </FormGroup>
        </Card>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalClose onClick={() => setShowOtpModal(false)}><X /></ModalClose>
            <Title style={{ marginBottom: '16px' }}>Verify {otpType === 'email' ? 'Email' : 'Phone'}</Title>
            <Description style={{ marginBottom: '24px' }}>
              We sent a verification code to your {otpType === 'email' ? email : countryCode + phoneNumber}.
            </Description>
            <FormGroup>
              <Label>Enter Verification Code</Label>
              <Input 
                type="text" 
                placeholder="6-digit code" 
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value)}
                maxLength={6}
              />
            </FormGroup>
            <ActionButton 
              style={{ width: '100%', marginTop: '24px' }}
              onClick={handleVerifyOtp}
              disabled={isLoading || otpValue.length < 6}
            >
              {isLoading ? "Verifying..." : "Confirm Verification"}
            </ActionButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Account;
